import * as amplitude from "@amplitude/analytics-browser";
import { Identify } from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import {
  AMPLITUDE_EVENTS,
  AmplitudeUserProperties,
} from "@constants/analytics";
import type { User } from "@lyteflo/types";
import type {
  CertificateDownloadEvent,
  QuickLookupSubmittedEvent,
} from "@portalTypes/analytics";
import { getAccountNameFromUser } from "@utils/analytics";
import { getDeviceInfo, isProd } from "@utils/environment";

const AMPLITUDE_API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY;

let isInitialized = false;

export const initAnalytics = () => {
  if (isInitialized) {
    return;
  }
  if (typeof window === "undefined") {
    return;
  }
  if (!isProd) {
    return;
  }
  if (!AMPLITUDE_API_KEY) {
    console.error("Amplitude API key is not set; analytics disabled.");
    return;
  }

  amplitude.add(sessionReplayPlugin());
  amplitude.init(AMPLITUDE_API_KEY, {
    // default options from Amplitude documentation
    autocapture: {
      attribution: true,
      fileDownloads: true,
      formInteractions: true,
      pageViews: true,
      sessions: true,
      elementInteractions: true,
      networkTracking: true,
      webVitals: true,
      frustrationInteractions: {
        thrashedCursor: true,
        errorClicks: true,
        deadClicks: true,
        rageClicks: true,
      },
    },
  });

  const { device_type, device_os } = getDeviceInfo();
  const identifyObj = new Identify()
    .set("device_type", device_type)
    .set("device_os", device_os);
  amplitude.identify(identifyObj);

  isInitialized = true;
};

export const setAmplitudeUserIdentity = (user: User) => {
  if (!user.email) {
    return;
  }

  amplitude.setUserId(user.email);

  const identifyObj = new Identify();

  identifyObj.set(AmplitudeUserProperties.EMAIL, user.email);
  identifyObj.set(
    AmplitudeUserProperties.ACCOUNT_NAME,
    getAccountNameFromUser(user),
  );

  amplitude.identify(identifyObj);
};

export const clearAmplitudeUserIdentity = () => {
  amplitude.reset();
};

export const trackEvent: typeof amplitude.track = (
  eventName,
  eventProperties,
  options,
) => {
  return amplitude.track(eventName, eventProperties, options);
};

export const trackCertificateDownload = (
  properties: CertificateDownloadEvent,
) => {
  trackEvent(AMPLITUDE_EVENTS.CLICK_DOWNLOAD_REPORT, properties);
};

export const trackQuickLookupSubmitted = (
  properties: QuickLookupSubmittedEvent,
) => {
  trackEvent(AMPLITUDE_EVENTS.QUICK_LOOKUP_SUBMITTED, properties);
};
